import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { embedDashboard, type EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { ActivitiesComponent } from '../../shared/components/activities/activities.component';
import { SupersetService } from '../../services/superset.service';
import { SandboxUsageDashboardComponent } from '@angular-workspace/feature';
import { environment } from '../../../environments/environment';
import { inject } from '@angular/core';
import { WorkspaceContextService } from '../../services/workspace-context.service';

interface AnalyticsDashboard {
  id: string;
  title: string;
  category: string;
  accent: string;
}

const ANALYTICS_DASHBOARDS: AnalyticsDashboard[] = [
  // Credit Request dashboard hidden while credit request flow is disabled
  // {
  //   id: environment.supersetDashboardId,
  //   title: 'Credit Request',
  //   category: 'Credits',
  //   accent: '#00d084',
  // },
  {
    id: environment.userActivitySupersetDashboardId,
    title: 'User Activity',
    category: 'Usage',
    accent: '#00b371',
  },
  {
    id: environment.computeRoleSupersetDashboardId,
    title: 'Compute Role',
    category: 'Compute',
    accent: '#059669',
  },
  {
    id: environment.organisationRequestsSupersetDashboardId,
    title: 'Organisation Requests',
    category: 'Organisations',
    accent: '#047857',
  },
  {
    id: environment.datasetAccessRequestsSupersetDashboardId,
    title: 'Dataset Access Requests',
    category: 'Access Control',
    accent: '#065f46',
  },
];

interface EmbedState {
  loading: boolean;
  error: string | null;
  embedded: EmbeddedDashboard | null;
  requestToken: number;
}

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    ActivitiesComponent,
    SandboxUsageDashboardComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class AdminDashboardComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('mount') private mountRefs?: QueryList<ElementRef<HTMLDivElement>>;

  private supersetService = inject(SupersetService);
  protected readonly workspace = inject(WorkspaceContextService);
  private ngZone = inject(NgZone);
  private viewReady = false;
  private readonly embedStates = new Map<string, EmbedState>(
    ANALYTICS_DASHBOARDS.map((d) => [d.id, { loading: false, error: null, embedded: null, requestToken: 0 }]),
  );

  readonly dashboards = ANALYTICS_DASHBOARDS;
  selectedDashboard: AnalyticsDashboard = ANALYTICS_DASHBOARDS[0];
  showSandbox = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.embedIfNeeded(this.selectedDashboard);
  }

  selectDashboard(dashboard: AnalyticsDashboard): void {
    this.showSandbox = false;
    if (dashboard.id === this.selectedDashboard.id) return;
    this.selectedDashboard = dashboard;
    void this.embedIfNeeded(dashboard);
  }

  selectSandbox(): void {
    this.showSandbox = true;
  }

  trackById(_: number, d: AnalyticsDashboard): string {
    return d.id;
  }

  isLoading(dashboardId: string): boolean {
    return this.embedStates.get(dashboardId)?.loading ?? false;
  }

  getError(dashboardId: string): string | null {
    return this.embedStates.get(dashboardId)?.error ?? null;
  }

  private getMountElement(dashboardId: string): HTMLDivElement | undefined {
    const index = this.dashboards.findIndex((d) => d.id === dashboardId);
    return this.mountRefs?.toArray()[index]?.nativeElement;
  }

  private async embedIfNeeded(dashboard: AnalyticsDashboard): Promise<void> {
    if (!this.viewReady || !this.workspace.canViewPlatformSuperset()) return;
    const state = this.embedStates.get(dashboard.id);
    if (!state || state.embedded || state.loading) return;

    const supersetDomain = environment.supersetDashboardUrl?.trim();
    const guestTokenEndpoint = environment.supersetGuestTokenEndpoint?.trim();
    const mountEl = this.getMountElement(dashboard.id);

    if (!mountEl || !supersetDomain || !guestTokenEndpoint) {
      state.error = 'Superset is not configured.';
      return;
    }

    const token = ++state.requestToken;
    state.loading = true;
    state.error = null;

    try {
      let guestTokenPromise: Promise<string> | null = this.supersetService.getGuestToken();
      const embedUuid = await this.supersetService.resolveEmbedUuid(dashboard.id);

      if (token !== state.requestToken) {
        return;
      }

      const embedded = await embedDashboard({
        id: embedUuid,
        supersetDomain: supersetDomain.replace(/\/+$/, ''),
        mountPoint: mountEl,
        fetchGuestToken: () => {
          const pending = guestTokenPromise ?? this.supersetService.getGuestToken();
          guestTokenPromise = null;
          return pending;
        },
        dashboardUiConfig: {
          hideTitle: true,
          urlParams: { standalone: '1', native_filters_key: '' },
        },
      });

      if (token !== state.requestToken) {
        embedded.unmount();
        return;
      }
      state.embedded = embedded;
    } catch (error) {
      if (token === state.requestToken) {
        console.error('[SupersetEmbed] failed to embed dashboard', dashboard.id, error);
        state.error = 'Unable to load dashboard. Please check guest token middleware.';
      }
    } finally {
      if (token === state.requestToken) {
        // embedDashboard() resolves off an iframe 'load'/postMessage handshake, which can
        // settle outside Angular's zone and leave the UI showing a stale spinner until the
        // next unrelated event. Re-enter the zone explicitly so the view updates immediately.
        this.ngZone.run(() => {
          state.loading = false;
        });
      }
    }
  }

  ngOnDestroy(): void {
    for (const state of this.embedStates.values()) {
      state.requestToken++;
      state.embedded?.unmount();
      state.embedded = null;
    }
  }
}
